import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef, useState } from 'react';
import GlassPanel from '../../../components/base/GlassPanel';
import PolygonBackground from '../../../components/feature/PolygonBackground';

export default function ContactSection() {
  const sectionRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    send_confirmation: 'false'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'message' && value.length > 500) return;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d5qiuqgtu5df1co4d9i0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData).toString()
      });

      if (response.ok) {
        setSubmitStatus('Message sent successfully! We\'ll get back to you soon.');
        setFormData({ name: '', email: '', company: '', message: '', send_confirmation: 'false' });
      } else {
        setSubmitStatus('Failed to send message. Please try again.');
      }
    } catch (error) {
      setSubmitStatus('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" ref={sectionRef} className="relative min-h-screen py-20 md:py-32 bg-gradient-to-b from-blue-900 to-blue-950 overflow-hidden">
      {/* Animated Polygonal Background */}
      <PolygonBackground 
        className="opacity-40" 
        dotColor="255, 255, 255"
        lineColor="255, 255, 255"
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid lg:grid-cols-2 gap-12 md:gap-20 items-start">
          {/* Left Column - Contact Information */}
          <div className="lg:pr-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h3 className="text-sm font-semibold text-white/60 tracking-widest uppercase mb-6">
                Contact
              </h3>
              
              <h2 className="text-4xl md:text-5xl font-light text-white mb-8 tracking-tight leading-tight">
                Let's Discuss Your Vision
              </h2>
              
              <p className="text-lg md:text-xl text-white/80 leading-relaxed mb-12 max-w-lg">
                For inquiries or to discuss potential opportunities, we're here to help you navigate the path to sustainable growth and long-term value creation.
              </p>
            </motion.div>
          </div>

          {/* Right Column - Contact Form */}
          <div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <GlassPanel 
                className="p-6 md:p-12 lg:p-16"
                blur={30}
                opacity={5}
                borderOpacity={25}
                glowColor="#169fdb"
              >
                <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6 md:space-y-8">
                  {/* Name Field */}
                  <div>
                    <label htmlFor="name" className="block text-white/70 text-sm font-medium mb-2">
                      Your Name <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter your full name"
                      required
                      className="w-full bg-white/10 border-0 border-b-2 border-white/30 px-0 py-4 md:py-5 text-white/90 text-base md:text-lg placeholder-white/50 focus:border-blue-400 focus:outline-none focus:ring-0 focus:shadow-lg transition-all duration-300"
                    />
                  </div>

                  {/* Email Field */}
                  <div>
                    <label htmlFor="email" className="block text-white/70 text-sm font-medium mb-2">
                      Email Address <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your.email@example.com"
                      required
                      className="w-full bg-white/10 border-0 border-b-2 border-white/30 px-0 py-4 md:py-5 text-white/90 text-base md:text-lg placeholder-white/50 focus:border-blue-400 focus:outline-none focus:ring-0 focus:shadow-lg transition-all duration-300"
                    />
                  </div>

                  {/* Company Field */}
                  <div>
                    <label htmlFor="company" className="block text-white/70 text-sm font-medium mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      placeholder="Your company name"
                      className="w-full bg-white/10 border-0 border-b-2 border-white/30 px-0 py-4 md:py-5 text-white/90 text-base md:text-lg placeholder-white/50 focus:border-blue-400 focus:outline-none focus:ring-0 focus:shadow-lg transition-all duration-300"
                    />
                  </div>

                  {/* Recipient Email (hidden) */}
                  <input type="hidden" name="recipient" value="chanawani@inocap.biz" />
                  
                  {/* Disable confirmation email (hidden) */}
                  <input type="hidden" name="send_confirmation" value="false" />

                  {/* Message Field */}
                  <div>
                    <label htmlFor="message" className="block text-white/70 text-sm font-medium mb-2">
                      Message <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Tell us about your project or inquiry..."
                      rows={4}
                      maxLength={500}
                      required
                      className="w-full bg-white/10 border-0 border-b-2 border-white/30 px-0 py-4 md:py-5 text-white/90 text-base md:text-lg placeholder-white/50 focus:border-blue-400 focus:outline-none focus:ring-0 focus:shadow-lg transition-all duration-300 resize-none"
                    />
                    <div className="text-right text-white/50 text-sm mt-2">
                      {formData.message.length}/500 characters
                    </div>
                  </div>

                  {/* Submit Button */}
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full bg-white text-blue-900 py-3 md:py-4 text-base md:text-lg font-semibold rounded-full hover:bg-blue-400 hover:text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Sending Message...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center">
                        Send Message
                        <i className="ri-arrow-right-line ml-2"></i>
                      </span>
                    )}
                  </motion.button>

                  {/* Status Message */}
                  {submitStatus && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`text-center py-3 px-4 rounded-lg ${
                        submitStatus.includes('successfully') 
                          ? 'bg-green-500/20 text-green-300 border border-green-500/30' 
                          : 'bg-red-500/20 text-red-300 border border-red-500/30'
                      }`}
                    >
                      {submitStatus}
                    </motion.div>
                  )}
                </form>
              </GlassPanel>
            </motion.div>
          </div>
        </div>

        {/* Footer */}
        <motion.footer
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
          className="mt-20 md:mt-32 pt-12 border-t border-white/20 text-center"
        >
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <img
                src="https://static.readdy.ai/image/04bffda83af79335128d062f8508dd8e/a7a2e18ca8072bae079c20fea1ca2a9d.png"
                alt="INOCAP"
                className="h-16 mx-auto md:mx-0"
              />
            </div>
            <div className="text-white/60 text-sm">
              <span>© 2025 INOCAP Ltd. All rights reserved.</span>
            </div>
          </div>
        </motion.footer>
      </div>
    </section>
  );
}
